import React from 'react';
import { Box, Button, Chip, Paper, Stack, Tooltip, Typography } from '@mui/material';
import ScienceIcon from '@mui/icons-material/Science';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import type { FoodTestId, FoodTestOutcome, ProcedureAction } from './types';

interface ApparatusAction {
  action: ProcedureAction;
  label: string;
}

interface FoodTestApparatusProps {
  sampleLabel: string;
  test: FoodTestId;
  outcome: FoodTestOutcome;
  isHeating: boolean;
  onWash: () => void;
  actions: ApparatusAction[];
  onAction: (action: ProcedureAction) => void;
}

const FoodTestApparatus: React.FC<FoodTestApparatusProps> = ({ sampleLabel, test, outcome, isHeating, onWash, actions, onAction }) => (
  <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1, borderColor: '#c9d9d3' }}>
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.25, borderBottom: '1px solid #dce7e2' }}>
      <Box>
        <Typography variant="subtitle1" fontWeight={800}>Laboratory bench</Typography>
        <Typography variant="caption" color="text.secondary">Food sample: {sampleLabel}</Typography>
      </Box>
      <Button size="small" startIcon={<WaterDropIcon />} onClick={onWash}>Wash apparatus</Button>
    </Stack>
    <Box sx={{ background: 'linear-gradient(180deg, #f7fbf9 0%, #e7efeb 100%)', px: 1, py: 2 }}>
      <Box component="svg" viewBox="0 0 760 330" role="img" aria-label="Virtual food tests laboratory apparatus" sx={{ display: 'block', width: '100%', maxHeight: 360 }}>
        <defs>
          <linearGradient id="glass" x1="0" x2="1"><stop offset="0" stopColor="#d6eceb" stopOpacity=".35"/><stop offset=".5" stopColor="#fff" stopOpacity=".8"/><stop offset="1" stopColor="#a8d4d1" stopOpacity=".4"/></linearGradient>
          <linearGradient id="bath-water" x1="0" x2="0" y1="0" y2="1"><stop offset="0" stopColor="#85c3d2" stopOpacity=".45"/><stop offset="1" stopColor="#3a91a2" stopOpacity=".68"/></linearGradient>
          <filter id="soft-shadow" x="-20%" y="-20%" width="140%" height="140%"><feDropShadow dx="0" dy="4" stdDeviation="3" floodColor="#23413c" floodOpacity=".16"/></filter>
        </defs>
        <rect x="22" y="265" width="716" height="20" rx="3" fill="#9b775c"/><rect x="22" y="285" width="716" height="9" fill="#74543d"/>
        <rect x="42" y="57" width="138" height="22" rx="4" fill="#fbfdff" stroke="#aabbb7"/><text x="52" y="72" fontSize="11" fill="#3e5651">REAGENTS</text>
        {[0, 1, 2, 3].map((index) => <g key={index} transform={`translate(${55 + index * 30}, 91)`} filter="url(#soft-shadow)">
          <rect x="0" y="0" width="22" height="58" rx="5" fill="url(#glass)" stroke="#79918d"/>
          <rect x="3" y="30" width="16" height="25" rx="3" fill={['#4c91cd', '#b17b26', '#647ac8', '#e1d38b'][index]} opacity=".76"/>
          <rect x="5" y="-7" width="12" height="8" rx="1" fill="#52645f"/>
        </g>)}
        <text x="49" y="169" fontSize="9" fill="#546862">Benedict’s  Iodine  Biuret  Others</text>

        <g transform="translate(255, 58)" filter="url(#soft-shadow)">
          <path d="M10 10 Q10 0 20 0 L106 0 Q116 0 116 10 L109 152 Q108 168 93 170 L33 170 Q18 168 17 152 Z" fill="url(#glass)" stroke="#68827c" strokeWidth="2"/>
          <path d="M17 86 Q66 81 112 86 L109 151 Q108 165 93 166 L33 166 Q19 164 18 150 Z" fill="url(#bath-water)"/>
          <line x1="17" y1="86" x2="112" y2="86" stroke="#559eaa" strokeWidth="2"/>
          <text x="28" y="187" fontSize="11" fill="#31564f">WATER BATH</text>
          <rect x="124" y="48" width="10" height="72" rx="5" fill="#fff" stroke="#617771"/>
          <rect x="127" y="86" width="4" height="27" fill="#d44538"/>
          <circle cx="129" cy="116" r="5" fill="#d44538"/>
          <text x="118" y="137" fontSize="8" fill="#31564f">THERMOMETER</text>
        </g>
        <g transform="translate(281, 38)">
          <path d="M15 0 L55 0 L52 23 L49 84 Q48 94 35 96 Q22 94 21 84 L18 23 Z" fill="url(#glass)" stroke="#53706b" strokeWidth="2"/>
          <path d="M21 54 Q35 50 50 54 L49 82 Q48 91 35 92 Q23 90 22 82 Z" fill={outcome.colour} opacity=".78" className={isHeating ? 'food-test-liquid food-test-heating' : 'food-test-liquid'} />
          {outcome.precipitate && <g className="food-test-precipitate"><circle cx="29" cy="82" r="3" fill="#9d3a27"/><circle cx="37" cy="86" r="3.5" fill="#a8402d"/><circle cx="43" cy="81" r="2.8" fill="#873121"/></g>}
          {outcome.emulsion && <path d="M23 65 Q35 59 49 65 L49 82 Q48 91 35 92 Q23 90 22 82 Z" fill="#fff" opacity=".62" className="food-test-cloud"/>}
          <path d="M18 23 Q35 19 52 23" fill="none" stroke="#73908a" strokeWidth="2"/>
          <text x="9" y="113" fontSize="9" fill="#31564f">{sampleLabel.slice(0, 16)}</text>
        </g>
        <g transform="translate(420, 54)">
          <rect x="0" y="130" width="134" height="38" rx="5" fill="#d5c2a7" stroke="#8e775b"/>
          {[0, 1, 2].map((index) => <g key={index} transform={`translate(${12 + index * 38}, 0)`}>
            <path d="M2 0 L26 0 L24 96 Q23 105 14 106 Q5 105 4 96 Z" fill="url(#glass)" stroke="#68827c"/>
            <path d="M5 65 L24 65 L23 96 Q22 102 14 103 Q7 102 6 96 Z" fill={index === 1 ? '#f0d7a6' : '#d5edf0'} opacity=".78"/>
            <path d="M0 -2 L28 -2" stroke="#52645f" strokeWidth="4"/>
          </g>)}
          <text x="18" y="185" fontSize="11" fill="#31564f">TEST-TUBE RACK</text>
        </g>

        <g transform="translate(586, 49)">
          <path d="M24 0 L48 0 L45 90 Q44 99 36 100 Q28 99 27 90 Z" fill="url(#glass)" stroke="#66817b" strokeWidth="2"/>
          <path d="M28 54 Q36 51 44 54 L43 89 Q42 96 36 97 Q30 96 29 89 Z" fill="#8cc4e6" opacity=".76"/>
          <rect x="26" y="-7" width="20" height="9" rx="2" fill="#718780"/>
          <text x="6" y="116" fontSize="10" fill="#31564f">DROPPER</text>
        </g>

        <g transform="translate(586, 192)">
          <path d="M36 0 C22 26 8 38 8 57 A28 28 0 0 0 64 57 C64 38 50 26 36 0Z" fill="#ef9c42" stroke="#b96226" strokeWidth="2"/>
          <path d="M36 25 C28 41 22 47 22 59 A14 14 0 0 0 50 59 C50 47 44 41 36 25Z" fill="#ffe3a1"/>
          <rect x="-3" y="78" width="78" height="5" rx="2" fill="#65756f"/>
          <text x="14" y="101" fontSize="10" fill="#31564f">BUNSEN BURNER</text>
        </g>
        <g className={isHeating ? 'food-test-steam is-active' : 'food-test-steam'} fill="none" stroke="#8fb2ad" strokeWidth="2" opacity=".75">
          <path d="M289 25 C279 15 300 12 290 2"/><path d="M309 25 C299 15 320 12 310 2"/><path d="M329 25 C319 15 340 12 330 2"/>
        </g>
        <text x="31" y="235" fontSize="10" fill="#536b65">REAGENT SHELF</text>
        <text x="243" y="241" fontSize="10" fill="#536b65">CONTROLLED WATER-BATH HEATING</text>
      </Box>
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ px: 1, pt: 1 }}>
        <Chip size="small" icon={<ScienceIcon />} label={`Test: ${test.replaceAll('-', ' ')}`} />
        <Chip size="small" label={`Observed colour: ${outcome.colourName}`} sx={{ backgroundColor: outcome.colour, color: outcome.colour === '#1677c8' || outcome.colour === '#142c65' || outcome.colour === '#a83b26' ? '#fff' : '#182e2a' }} />
        {isHeating && <Chip size="small" color="warning" label="Water bath active" />}
        <Tooltip title="Colour labels accompany each visual state for colour-blind accessibility.">
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Text labels identify every result.</Typography>
        </Tooltip>
      </Stack>
    </Box>
    <Box
      className="food-tube-drop-target"
      onDragOver={(event) => event.preventDefault()}
      onDrop={(event) => {
        event.preventDefault();
        const action = event.dataTransfer.getData('application/x-food-lab-action') as ProcedureAction;
        if (actions.some((item) => item.action === action)) onAction(action);
      }}
      sx={{ px: 2, py: 1.5, borderTop: '1px solid #dce7e2', textAlign: 'center' }}
    >
      <Typography variant="caption" color="text.secondary">Drop the selected reagent over the tube, or click its label to add it.</Typography>
      <Stack direction="row" justifyContent="center" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
        {actions.map((item, index) => <Button
          key={`${item.action}-${index}`}
          size="small"
          variant="outlined"
          draggable
          onDragStart={(event) => event.dataTransfer.setData('application/x-food-lab-action', item.action)}
          onClick={() => onAction(item.action)}
          sx={{ cursor: 'grab' }}
        >{item.label}</Button>)}
      </Stack>
    </Box>
  </Paper>
);

export default FoodTestApparatus;